// React
import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
// Third party imports
import { useDispatch } from "react-redux";

// MUI
import Button from "@mui/material/Button";

// Components
import Modal from "components/ui/modal";

// Redux
import { setError } from "redux/slices/snackbarSlice";

// API
import { listNotificationsPreferences, updateNotificationsPreferences } from "api/notifications";

const NotificationSettings = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("notificationPopover");

  // State
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(false);

  // Hooks
  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await listNotificationsPreferences();

      if (response?.data) {
        const transformed = Object.entries(response.data).map(([key, obj]) => ({
          id: key,
          name: obj.verbose_name,
          enabled: obj.value,
          description:
            obj.description || t("receiveAlertsFor", { name: obj.verbose_name.toLowerCase() }),
        }));
        setPreferences(transformed);
      }
    } catch (error) {
      dispatch(setError(t("failedToFetchPreferences")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPreferences();
    }
  }, [open]);

  // Handlers
  const handleToggle = async (id, checked) => {
    try {
      // Optimistically update UI
      const newPreferences = preferences.map((preference) => {
        if (preference.id === id) {
          return { ...preference, enabled: checked };
        }
        return preference;
      });
      setPreferences(newPreferences);

      // Update backend
      await updateNotificationsPreferences({
        [id]: checked,
      });
    } catch (error) {
      // Revert on error
      fetchPreferences();
      dispatch(setError(t("failedToUpdatePreference")));
    }
  };

  const footer = (
    <div className="flex justify-end">
      <Button variant="contained" onClick={onClose}>
        {t("done")}
      </Button>
    </div>
  );

  const header = (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-gray-900 pr-8">{t("notificationPreferences")}</h2>
      <p className="text-gray-600 text-sm leading-relaxed">
        {t("notificationPreferencesDescription")}
      </p>
    </div>
  );

  const content = (
    <div className="w-full max-w-2xl">
      {/* Preferences List */}
      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin motion-reduce:animate-none rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">{t("loadingPreferences")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {preferences.map((preference) => (
            <div
              key={preference.id}
              className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
            >
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  id={preference.id}
                  checked={preference.enabled}
                  onChange={(e) => handleToggle(preference.id, e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-primary"
                />
              </div>

              <div className="min-w-0 flex-1">
                <label
                  htmlFor={preference.id}
                  className="block text-sm font-medium text-gray-900 cursor-pointer mb-1"
                >
                  {preference.name}
                </label>
                <p className="text-sm leading-relaxed break-words text-gray-600">{preference.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title={t("notificationSettings")}
      onClose={onClose}
      maxWidth="min(600px, calc(100vw - 2rem))"
      height="auto"
      maxHeight="calc(100dvh - 2rem)"
      showCloseButton={true}
      className="notification-settings-modal"
      header={header}
      footer={footer}
    >
      {content}
    </Modal>
  );
};

export default NotificationSettings;
